import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { ListingsService } from './service/listings.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IAccount } from 'src/app/acct-comps/accounts.interfaces';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ListingDetailsComponent } from './components/listing-details/listing-details.component';
import { set } from 'lodash-es';

@Component({
  selector: 'app-listings',
  templateUrl: 'listings.component.html',
  styleUrls: ['listings.component.scss']
})
export class ListingsComponent implements OnInit, OnDestroy {
  private userLoginId: number;
  private currentAccount: number;
  private ngUnsubscribe$: Subject<any> = new Subject();

  public isLoadingData: boolean = true;
  public listingsList: any[] = [];
  public selListing: any;

  public tableWidth: string;
  public tableHeight: string;

  public filters: any = {
    store_code: '',
    address: '',
    business_name: '',
    city: '',
    state: '',
    postal_code: '',
    match_status: '',
    publisher: ''
  };

  public generateMatchesPublisher: string = '';

  public matchStatusOptions = [
    { label: 'All', value: '' },
    { label: 'Complete', value: 'complete' },
    { label: 'Pending', value: 'pending' },
    { label: 'Error', value: 'error' },
    { label: 'Failed', value: 'failed' },
    { label: 'Creating', value: 'creating' },
    { label: 'Created', value: 'created' },
    { label: 'Not Selected', value: 'not selected' }
  ];

  public publisherOptions: { label: string, value: string }[] = [];
  public generateMatchesPublisherOptions: { label: string, value: string }[] = [];

  constructor(
    private router: Router,
    private listingService: ListingsService,
    private sessionService: SessionService,
    private notifyService: NotifyService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.onResize({ target: window });
    this.getCurrentAccountDetails();
    this.getCurrentUserDetails();
    this.getAllListings();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next(null);
    this.ngUnsubscribe$.complete();
  }

  private getCurrentAccountDetails(): void {
    this.sessionService
      .getSelectedAccount$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((account: IAccount) => {
        this.currentAccount = account?._id;
      });
  }

  private getCurrentUserDetails(): void {
    this.sessionService
      .getCurrentUser$()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
      });
  }

  get filteredListings() {
    return this.listingsList.filter((listing) => {
      return Object.keys(this.filters).every((key) => {
        const filterValue = this.filters[key].toLowerCase();
        if (!filterValue) return true; // Skip empty filters
        const value = String(listing[key] ?? '').toLowerCase();
        if (key === 'match_status') {
          return value === filterValue;
        }
        else {
          return value.includes(filterValue);
        }
      });
    });
  }

  private getAllListings(): void {
    this.isLoadingData = true;
    if (!this.currentAccount) {
      this.notifyService.info('Please select an account to view listings.');
      this.isLoadingData = false;
      return;
    }

    this.listingService
      .loadAllListings(this.currentAccount, this.userLoginId)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (response) => {
          this.listingsList = response['listings'];
          this.isLoadingData = false;
          if (this.listingsList.length) {
            this.notifyService.success('Listings loaded successfully.');
            const publishers = Array.from(new Set(this.listingsList.map(l => l.publisher)));
            this.publisherOptions = [{ label: 'All', value: '' }, ...publishers.map(p => ({ label: this.toTitleCase(p), value: p }))];
            this.generateMatchesPublisherOptions = publishers.map(p => ({ label: this.toTitleCase(p), value: p }));
          } else {
            this.notifyService.info('Listings not found.');
          }
        },
        error: () => {
          this.notifyService.error('Error while fetching listings.');
          this.isLoadingData = false;
        }
      });
  }

  public generateMatchesForAllListings(): void {
    this.isLoadingData = true;
    this.listingService
      .generateListingMatchesForAll(this.currentAccount, this.userLoginId, this.generateMatchesPublisher)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: (response) => {
          setTimeout(() => {
            this.isLoadingData = false;
          }, 120000);
        },
        error: (error) => {
          this.notifyService.error('Error generating matches for all listings.');
          console.error('Error generating matches:', error);
          this.isLoadingData = false;
        }
      });
    }

  public refreshListings(): void {
    this.listingsList = [];
    this.selListing = null;
    this.getAllListings();
  }

  normalizeAddress(address: string[] | string | null | undefined): string {
    if (Array.isArray(address)) {
      const filtered = address.filter(a => !!a);
      return filtered.length > 0 ? filtered.join(', ') : 'Address unavailable';
    }

    if (typeof address === 'string' && address.trim().length > 0) {
      return address;
    }

    return 'Address unavailable';
  } 

  toTitleCase(str: string): string {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  onRowSelect(event: any): void {
    const listing = event.data;
    this.dialogService.open(ListingDetailsComponent, {
      data: { userId: this.userLoginId, accountId: this.currentAccount, listing },
      //header: `${this.toTitleCase(listing.publisher)} Listing Details`,
      width: '70%',
      height: '80%',
      contentStyle: { 'max-height': '80vh', overflow: 'auto' },
      baseZIndex: 10000
    }).onClose.subscribe((result) => {
      console.log('Dialog closed with result:', result);
    })
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
  const width = Math.min(event.target.innerWidth, 1900);
  const height = event.target.innerHeight;

  this.tableWidth = width - 100 + 'px';
  this.tableHeight = (height - 650) + 'px'; // adjust this buffer to fit your header/filters
}
}
